import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import type { BusinessSearchResponse } from '@/types';

const FEATURED_CATEGORIES = [
  { slug: 'clothing-apparel', name: 'Clothing & Apparel', icon: '👔' },
  { slug: 'food-beverages', name: 'Food & Beverages', icon: '🍽️' },
  { slug: 'electronics', name: 'Electronics', icon: '📱' },
  { slug: 'health-beauty', name: 'Health & Beauty', icon: '💄' },
  { slug: 'home-services', name: 'Home Services', icon: '🔧' },
  { slug: 'automobile', name: 'Automobile', icon: '🚗' },
];

export default function Home() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const { data: featured } = useQuery({
    queryKey: ['home-featured'],
    queryFn: () =>
      apiClient<BusinessSearchResponse>('/public/businesses/search', {
        params: { q: '', page: 1, page_size: 6 },
      }),
    staleTime: 60_000,
    retry: 1,
  });

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-primary via-brand-secondary to-slate-900 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-white blur-3xl" />
          <div className="absolute -right-10 bottom-0 h-96 w-96 rounded-full bg-brand-accent blur-3xl" />
        </div>

        <div className="container-page relative py-20 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-medium uppercase tracking-widest text-blue-200">
              Discover local businesses
            </p>
            <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Find shops & services across India
            </h1>
            <p className="mt-4 text-lg text-blue-100">
              Scan a QR code or search by name, city, or category. Connect with verified local businesses instantly.
            </p>

            <form onSubmit={handleSearch} className="mx-auto mt-10 flex max-w-xl flex-col gap-3 sm:flex-row">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search businesses, products, cities..."
                className="flex-1 rounded-brand border-0 px-4 py-3 text-slate-900 shadow-lg focus:outline-none focus:ring-2 focus:ring-white/50"
                aria-label="Search businesses"
              />
              <Button type="submit" variant="secondary" size="lg" className="shrink-0">
                Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="container-page py-16">
        <h2 className="text-2xl font-bold text-slate-900">Browse by category</h2>
        <p className="mt-2 text-slate-600">Explore businesses in popular categories</p>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {FEATURED_CATEGORIES.map(({ slug, name, icon }) => (
            <Link
              key={slug}
              to={`/category/${slug}`}
              className="group flex flex-col items-center rounded-brand border border-slate-200 bg-white p-6 text-center transition hover:border-brand-primary hover:shadow-md"
            >
              <span className="text-3xl" aria-hidden>{icon}</span>
              <span className="mt-3 text-sm font-medium text-slate-700 group-hover:text-brand-primary">
                {name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {featured && featured.data.length > 0 && (
        <section className="bg-white py-16">
          <div className="container-page">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Featured businesses</h2>
                <p className="mt-2 text-slate-600">Popular listings on QRBusiness India</p>
              </div>
              <Link to="/search" className="text-sm font-medium text-brand-primary hover:underline">
                View all →
              </Link>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {featured.data.map((business) => (
                <article
                  key={business.id}
                  className="rounded-brand border border-slate-200 bg-slate-50 p-5 transition hover:border-brand-primary hover:shadow-sm"
                >
                  <h3 className="font-semibold text-slate-900">{business.name}</h3>
                  {business.category && (
                    <p className="mt-1 text-sm text-slate-500">{business.category}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-sm text-amber-600">★ {business.rating_avg.toFixed(1)}</span>
                    <Link
                      to={
                        business.state_slug && business.city_slug
                          ? `/india/${business.state_slug}/${business.city_slug}/${business.slug}`
                          : `/search?q=${encodeURIComponent(business.name)}`
                      }
                      className="text-sm font-medium text-brand-primary hover:underline"
                    >
                      View
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="container-page py-16">
        <div className="rounded-2xl bg-slate-900 px-8 py-12 text-center text-white sm:px-16">
          <h2 className="text-2xl font-bold">Own a local business?</h2>
          <p className="mx-auto mt-3 max-w-lg text-slate-300">
            Get your free digital storefront and QR code. Reach customers who scan your shop QR.
          </p>
          <Button
            className="mt-6"
            variant="primary"
            size="lg"
            onClick={() => navigate('/contact')}
          >
            List your business
          </Button>
        </div>
      </section>
    </div>
  );
}
